import type {
  ChoiceRole,
  FormOption,
  FormSectionSchema,
  MultiSelectField,
  SingleSelectField,
} from '../types/form-section';
import { FIELD_KEY_PATTERN } from '../types/form-section';
import type { SensagramAttachmentInputBinding } from '../types/sensagram-attachment';

export const UNSELECTED_CHOICE_CONTEXT_MIN_INSTRUCTION_LINES = 2;
export const UNSELECTED_CHOICE_CONTEXT_MAX_INSTRUCTION_LINES = 4;
export const UNSELECTED_CHOICE_CONTEXT_MAX_INSTRUCTION_SCALARS = 200;
export const UNSELECTED_CHOICE_PROJECTION_CHAR_LIMIT = 3_000;

export interface UnselectedChoicePromptRecord {
  field_label: string;
  guidance_lines: string[];
  unselected_labels: string[];
  answer_role?: Exclude<ChoiceRole, 'non_response'>;
}

export interface UnselectedChoiceBudgetContributor {
  source_node_key: string;
  field_key: string;
}

export interface AttachmentUnselectedChoiceBudget {
  attachment_node_key: string;
  rendered_characters: number;
  projection: UnselectedChoicePromptRecord[];
  contributors: UnselectedChoiceBudgetContributor[];
}

export interface UnselectedChoiceBudgetNode {
  node_key: string;
  node_type: string;
  position: number;
  config?: FormSectionSchema | Record<string, unknown>;
  input_binding?: SensagramAttachmentInputBinding | Record<string, unknown>;
}

type PromptJsonValue =
  | null
  | boolean
  | string
  | PromptJsonValue[]
  | { [key: string]: PromptJsonValue };

export function unicodeScalarLength(value: string): number {
  return Array.from(value).length;
}

export function isSpecialChoiceRole(
  role: ChoiceRole | string | null | undefined,
): role is ChoiceRole {
  return role === 'selects_none' || role === 'selects_all' || role === 'non_response';
}

export function isMutuallyExclusiveMultiSelectOption(option: FormOption): boolean {
  return option.exclusive === true || isSpecialChoiceRole(option.choice_role);
}

function hex4(value: number): string {
  return value.toString(16).padStart(4, '0');
}

function renderPythonJsonString(value: string): string {
  let rendered = '"';
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (character === '"') rendered += '\\"';
    else if (character === '\\') rendered += '\\\\';
    else if (codePoint === 0x08) rendered += '\\b';
    else if (codePoint === 0x09) rendered += '\\t';
    else if (codePoint === 0x0a) rendered += '\\n';
    else if (codePoint === 0x0c) rendered += '\\f';
    else if (codePoint === 0x0d) rendered += '\\r';
    else if (codePoint < 0x20 || codePoint >= 0x7f) {
      if (codePoint <= 0xffff) rendered += `\\u${hex4(codePoint)}`;
      else {
        const adjusted = codePoint - 0x10000;
        const high = 0xd800 + (adjusted >> 10);
        const low = 0xdc00 + (adjusted & 0x3ff);
        rendered += `\\u${hex4(high)}\\u${hex4(low)}`;
      }
    } else rendered += character;
  }
  return `${rendered}"`;
}

function comparePythonStrings(left: string, right: string): number {
  const leftCodePoints = Array.from(left, (value) => value.codePointAt(0) ?? 0);
  const rightCodePoints = Array.from(right, (value) => value.codePointAt(0) ?? 0);
  const sharedLength = Math.min(leftCodePoints.length, rightCodePoints.length);
  for (let index = 0; index < sharedLength; index += 1) {
    if (leftCodePoints[index] !== rightCodePoints[index]) {
      return leftCodePoints[index] - rightCodePoints[index];
    }
  }
  return leftCodePoints.length - rightCodePoints.length;
}

function renderPromptJsonValue(value: PromptJsonValue, depth: number): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return renderPythonJsonString(value);
  const currentIndent = ' '.repeat(depth * 2);
  const childIndent = ' '.repeat((depth + 1) * 2);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return `[
${value.map((item) => `${childIndent}${renderPromptJsonValue(item, depth + 1)}`).join(',\n')}
${currentIndent}]`;
  }
  const keys = Object.keys(value).sort(comparePythonStrings);
  if (keys.length === 0) return '{}';
  return `{
${keys
  .map(
    (key) =>
      `${childIndent}${renderPythonJsonString(key)}: ${renderPromptJsonValue(value[key], depth + 1)}`,
  )
  .join(',\n')}
${currentIndent}}`;
}

/** Exact parity with Python json.dumps(indent=2, sort_keys=True, ensure_ascii=True). */
export function renderPythonCompatiblePromptJson(value: PromptJsonValue): string {
  return renderPromptJsonValue(value, 0);
}

export function renderUnselectedChoiceProjection(
  records: UnselectedChoicePromptRecord[],
): string {
  return renderPythonCompatiblePromptJson(records as unknown as PromptJsonValue);
}

function escapedLabelLength(label: string): number {
  return renderPythonCompatiblePromptJson(label).length;
}

function promptRecord(
  field: SingleSelectField | MultiSelectField,
  unselectedLabels: string[],
  answerRole?: Exclude<ChoiceRole, 'non_response'>,
): UnselectedChoicePromptRecord {
  const record: UnselectedChoicePromptRecord = {
    field_label: field.label,
    guidance_lines: [...(field.unselected_choice_context?.instruction_lines ?? [])],
    unselected_labels: [...unselectedLabels],
  };
  if (answerRole) record.answer_role = answerRole;
  return record;
}

function ordinaryAnswerRecord(
  field: SingleSelectField | MultiSelectField,
): UnselectedChoicePromptRecord | null {
  const ordinaryOptions = field.options.filter((option) => !isSpecialChoiceRole(option.choice_role));
  if (ordinaryOptions.length === 0) return null;
  const selectedCount =
    field.field_type === 'multi_select'
      ? Math.max(1, field.validation.min_selections ?? 0)
      : 1;
  if (selectedCount > ordinaryOptions.length) return null;
  const selectedIndexes = new Set(
    ordinaryOptions
      .map((option, index) => ({ index, length: escapedLabelLength(option.label) }))
      .sort((left, right) => left.length - right.length || left.index - right.index)
      .slice(0, selectedCount)
      .map(({ index }) => index),
  );
  return promptRecord(
    field,
    ordinaryOptions
      .filter((_option, index) => !selectedIndexes.has(index))
      .map((option) => option.label),
  );
}

export function maximumPromptRecordForField(
  field: SingleSelectField | MultiSelectField,
): UnselectedChoicePromptRecord | null {
  if (!field.unselected_choice_context) return null;
  const candidates: UnselectedChoicePromptRecord[] = [];
  const ordinary = ordinaryAnswerRecord(field);
  if (ordinary) candidates.push(ordinary);

  const ordinaryLabels = field.options
    .filter((option) => !isSpecialChoiceRole(option.choice_role))
    .map((option) => option.label);
  const roles = new Set(field.options.map((option) => option.choice_role));
  if (roles.has('selects_none')) {
    candidates.push(promptRecord(field, ordinaryLabels, 'selects_none'));
  }
  if (roles.has('selects_all')) {
    candidates.push(promptRecord(field, [], 'selects_all'));
  }
  if (candidates.length === 0) return null;
  return candidates.reduce((largest, candidate) =>
    renderUnselectedChoiceProjection([candidate]).length >
    renderUnselectedChoiceProjection([largest]).length
      ? candidate
      : largest,
  );
}

function isFormSectionSchema(value: unknown): value is FormSectionSchema {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { schema_version?: unknown }).schema_version === 'form_section.v1' &&
    Array.isArray((value as { fields?: unknown }).fields)
  );
}

function normalizedNodeKey(node: UnselectedChoiceBudgetNode): string {
  return String(node.node_key ?? '').trim();
}

function normalizedNodeType(node: UnselectedChoiceBudgetNode): string {
  return String(node.node_type ?? '').trim();
}

function normalizedBinding(value: unknown): SensagramAttachmentInputBinding | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Partial<SensagramAttachmentInputBinding>;
  const mode = candidate.mode;
  if (
    mode !== 'none' &&
    mode !== 'from_prior_nodes' &&
    mode !== 'sensacore_policy'
  ) {
    return null;
  }
  if (
    !Array.isArray(candidate.source_node_keys)
  ) {
    return null;
  }
  const sourceNodeKeys = candidate.source_node_keys.map((key) => String(key ?? '').trim());
  if (
    sourceNodeKeys.some((key) => !key || !FIELD_KEY_PATTERN.test(key)) ||
    new Set(sourceNodeKeys).size !== sourceNodeKeys.length
  ) {
    return null;
  }
  const policyKey = String(candidate.policy_key ?? '').trim() || null;
  if (policyKey !== null && policyKey.length > 120) return null;
  if (mode === 'none' && sourceNodeKeys.length > 0) return null;
  if (mode !== 'none' && sourceNodeKeys.length === 0 && policyKey === null) {
    return null;
  }
  return {
    mode,
    source_node_keys: sourceNodeKeys,
    ...(policyKey === null ? {} : { policy_key: policyKey }),
  };
}

export function estimateCompositionUnselectedChoiceBudgets(
  nodes: UnselectedChoiceBudgetNode[],
): AttachmentUnselectedChoiceBudget[] {
  const orderedNodes = [...nodes].sort((left, right) => left.position - right.position);
  const formSchemas = new Map<string, FormSectionSchema>();
  for (const node of orderedNodes) {
    if (normalizedNodeType(node) === 'form_section' && isFormSectionSchema(node.config)) {
      formSchemas.set(normalizedNodeKey(node), node.config);
    }
  }

  const budgets: AttachmentUnselectedChoiceBudget[] = [];
  orderedNodes.forEach((attachment, attachmentIndex) => {
    if (normalizedNodeType(attachment) !== 'sensagram_attachment') return;
    const binding = normalizedBinding(attachment.input_binding);
    if (!binding || binding.mode === 'none' || binding.source_node_keys.length === 0) return;
    const boundKeys = new Set(binding.source_node_keys);
    const projection: UnselectedChoicePromptRecord[] = [];
    const contributors: UnselectedChoiceBudgetContributor[] = [];
    for (const sourceNode of orderedNodes.slice(0, attachmentIndex)) {
      const sourceNodeKey = normalizedNodeKey(sourceNode);
      if (!boundKeys.has(sourceNodeKey)) continue;
      const schema = formSchemas.get(sourceNodeKey);
      if (!schema) continue;
      for (const field of schema.fields) {
        if (field.field_type !== 'single_select' && field.field_type !== 'multi_select') continue;
        const record = maximumPromptRecordForField(field);
        if (!record) continue;
        projection.push(record);
        contributors.push({
          source_node_key: sourceNodeKey,
          field_key: field.field_key,
        });
      }
    }
    if (projection.length === 0) return;
    budgets.push({
      attachment_node_key: normalizedNodeKey(attachment),
      rendered_characters: renderUnselectedChoiceProjection(projection).length,
      projection,
      contributors,
    });
  });
  return budgets;
}
